/**
 * R-75 — per-claim caveat honesty: declared vs actual confidence bands.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SiteRagReport } from '../../types/siteRag';
import type { CitationEvidenceCache } from './citationIntegrityVerifier';
import { buildCitationEvidenceCache } from './citationIntegrityVerifier';
import {
  detectDeclaredBandFromPhrases,
  scoreClaimConfidence,
  type ClaimConfidenceScore,
} from './claimConfidenceScorer';
import {
  bandGap,
  CONFIDENCE_BANDS,
  CONFIDENCE_BAND_ORDER,
  CAVEAT_PHRASE_MAP,
  normalizeCommentBand,
  type ConfidenceBand,
} from './confidenceTaxonomy';
import { reportFullText, isSerializedRecord } from './reportTextExtract';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { logAuditEvent } from '../telemetry/auditTelemetry';

/** RNH-CONDITIONAL-EXEMPT — softened conditional phrases are not unhedged. */
export const CONDITIONAL_SOFTENED_PATTERN = /\bconditional\s+(on|upon|to)\b/i;

const UNHEDGED_CONDITIONAL_PATTERN = /\bconditional\b/i;

const CONFIDENCE_COMMENT_RE = /<!--\s*confidence:\s*([^>]+?)\s*-->/gi;
const REF_TOKEN_RE = /\[Ref\s+(\d+)\]/gi;

export interface CaveatedClaim {
  id: string;
  excerpt: string;
  declaredBand: ConfidenceBand;
  declaredSource: 'html-comment' | 'caveat-phrase';
  /**
   * Exact reader-body sentence used as the rewrite anchor. HTML confidence
   * comments are retained here even though `excerpt` removes them for display.
   */
  matchText: string;
  refIndices: number[];
  sectionId?: string;
  lineStart: number;
  lineEnd: number;
  /** Character offset in source markdown for rewrite anchoring. */
  startOffset: number;
  endOffset: number;
}

export interface CaveatHonestyFinding {
  claimId: string;
  severity: 'block' | 'warn';
  declaredBand: ConfidenceBand;
  actualBand: ConfidenceBand;
  bandGap: number;
  excerpt: string;
  message: string;
  sectionId?: string;
  lineStart: number;
  lineEnd: number;
  evidenceBreakdown: ClaimConfidenceScore['breakdown'];
  suggestedRewrite?: string;
  /**
   * UNTRUNCATED source sentence (2026-08-14). `excerpt` is capped at 280
   * chars for display; anchoring a body rewrite on the truncated string
   * spliced `suggestedRewrite` (built from the FULL sentence) over only part
   * of the original and duplicated the tail into the persisted report.
   * Rewrites anchor on this; UI keeps using `excerpt`.
   */
  matchText?: string;
  kind?: 'band-gap' | 'unhedged-conditional';
}

export interface CaveatHonestyQaResult {
  findings: CaveatHonestyFinding[];
  claimsScanned: number;
  checkedAt: string;
  autocorrectApplied?: boolean;
  blockCount: number;
  warnCount: number;
}

function extractRefIndices(text: string): number[] {
  const out = new Set<number>();
  for (const m of text.matchAll(new RegExp(REF_TOKEN_RE.source, 'gi'))) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n)) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

function splitSentences(text: string): Array<{ text: string; start: number; end: number }> {
  const sentences: Array<{ text: string; start: number; end: number }> = [];
  /*
   * An HTML confidence comment opens with `<!--`. Treating the `!` as sentence
   * punctuation split the opening `<` away from the rest of the comment. The
   * resulting claim anchor started `-- confidence: high -->`, so the safe
   * rewrite could never find the real body text and a correctable 3-band gap
   * held the live Power report. Replace only that marker with a same-length
   * sentinel while splitting; offsets therefore remain byte-for-byte aligned
   * with the original source.
   */
  const confidenceCommentSentinel = '<\uE000--';
  const decimalPointSentinel = '\uE001';
  const splitSource = text
    .replace(/<!--/g, confidenceCommentSentinel)
    // Decimal points are not sentence boundaries. The live Power body split
    // `466.1 MW` into a synthetic `1 MW` claim and inherited the following
    // confidence comment, leaving a blocker after the real claims were fixed.
    .replace(/(?<=\d)\.(?=\d)/g, decimalPointSentinel);
  const re = /[^.!?\n]+(?:[.!?](?:\s|$)|$)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(splitSource)) !== null) {
    const chunk = m[0]
      .replaceAll(confidenceCommentSentinel, '<!--')
      .replaceAll(decimalPointSentinel, '.')
      .trim();
    if (chunk.length >= 20) {
      sentences.push({ text: chunk, start: m.index, end: m.index + m[0].length });
    }
  }
  if (sentences.length === 0 && text.trim().length >= 20) {
    sentences.push({ text: text.trim(), start: 0, end: text.length });
  }
  return sentences;
}

function lineNumberAt(text: string, offset: number): number {
  return text.slice(0, offset).split('\n').length;
}

const VERIFICATION_ADVERBS = '(?:(?:yet|independently|formally|fully|currently|still)\\s+){0,2}';

/**
 * P3-15 — an absence or lack that directly governs the verification phrase:
 * "the absence of a confirmed …", "lacks a verified …", "absent any confirmed …".
 */
const GOVERNING_ABSENCE_RE = new RegExp(
  `\\b(?:(?:absence|lack)\\s+of|absent|lack(?:s|ed|ing)?)\\s+(?:(?:a|an|any)\\s+)?${VERIFICATION_ADVERBS}$`,
  'i',
);

/**
 * Any negator earlier in the same clause turns an absence back into a
 * positive assertion: "doesn't lack a confirmed …", "no longer lacks …", "not
 * any lack of …", "no real lack of …", "far from lacking …". The whole clause
 * is searched, not only the words next to the absence, so an intervening
 * adverb, determiner or auxiliary cannot hide the double negative.
 */
const ABSENCE_NEGATOR_RE = /\b(?:not|no|never|without|hardly|barely|scarcely|cannot|neither|nor)\b|\b\w+n['’]t\b|\bfar\s+from\b/i;

/**
 * P3-15 — absence and lack negate the verification they govern, exactly like
 * "no"/"without": "the absence of a confirmed water source" states that no
 * confirmed source exists. Live environmental body: the autocorrect read that
 * caveat as a high-confidence assertion and rewrote it to "the absence of a
 * unverified water source". A negated absence stays scored.
 */
function isGovernedByUnnegatedAbsence(before: string): boolean {
  const absence = GOVERNING_ABSENCE_RE.exec(before);
  return absence !== null && !ABSENCE_NEGATOR_RE.test(before.slice(0, absence.index));
}

/** A verification requirement or its negation does not assert verification. */
function isNonAssertedVerification(text: string, index: number, phrase: string): boolean {
  if (!/^(?:confirmed|verified)$/i.test(phrase)) return false;
  const before = text.slice(0, index)
    .split(/[;:,]|\b(?:but|however|whereas|and)\b/i).at(-1) ?? '';
  const adverbs = VERIFICATION_ADVERBS;
  return new RegExp(`\\b(?:not|never|\\w+n['’]t)\\s+${adverbs}(?:been\\s+)?${adverbs}$`, 'i').test(before)
    || new RegExp(`\\b(?:no|without)\\s+(?:(?:a|an|any)\\s+)?${adverbs}$`, 'i').test(before)
    || isGovernedByUnnegatedAbsence(before)
    || /\b(?:no|neither)\s+(?:[\w'’-]+\s+){0,10}(?:has|have|had|is|are|was|were)\s+(?:yet\s+)?(?:been\s+)?$/i.test(before)
    || new RegExp(`\\b(?:must|should|needs?\\s+to|has\\s+to|have\\s+to)\\s+be\\s+${adverbs}$`, 'i').test(before)
    || /\b(?:if|when|once)\s+(?:(?:it|this|that)\s+is\s+)?$/i.test(before)
    || /\bonly\s+(?:if|when|while|in)\s+(?:(?:a|the)\s+)?$/i.test(before)
    || /\bnot\s+(?:yet\s+)?established\s+in\s+(?:(?:a|an|any|the)\s+)?$/i.test(before);
}

function detectAssertedBand(text: string): ConfidenceBand | null {
  // Mask only the non-asserting token, not its sentence. A separate positive
  // assertion ("no water right is confirmed, but power is verified") still faces
  // the normal evidence test. Explicit HTML declarations remain authoritative.
  const assertedText = text.replace(/\b(?:confirmed|verified)\b/gi, (phrase, index: number) => (
    isNonAssertedVerification(text, index, phrase) ? ' '.repeat(phrase.length) : phrase
  ));
  return detectDeclaredBandFromPhrases(assertedText);
}

/** Extract claims with explicit declared confidence (HTML comment or caveat phrase). */
export function extractCaveatedClaims(markdown: string): CaveatedClaim[] {
  const claims: CaveatedClaim[] = [];
  const commentBands: Array<{ start: number; end: number; band: ConfidenceBand }> = [];
  for (const m of markdown.matchAll(new RegExp(CONFIDENCE_COMMENT_RE.source, 'gi'))) {
    const band = normalizeCommentBand(m[1] ?? '');
    if (band) {
      const start = m.index ?? 0;
      commentBands.push({ start, end: start + m[0].length, band });
    }
  }

  const sentences = splitSentences(markdown);
  let claimIdx = 0;
  for (const sent of sentences) {
    let declaredBand: ConfidenceBand | null = null;
    let declaredSource: CaveatedClaim['declaredSource'] = 'caveat-phrase';

    const nearestComment = commentBands
      .filter(({ start, end }) => (
        (start >= sent.start && start <= sent.end)
        || (end <= sent.start && markdown.slice(end, sent.start).trim().length === 0)
      ))
      .sort((a, b) => Math.abs(a.start - sent.start) - Math.abs(b.start - sent.start))[0];
    if (nearestComment) {
      declaredBand = nearestComment.band;
      declaredSource = 'html-comment';
    }

    if (!declaredBand) {
      declaredBand = detectAssertedBand(sent.text);
    }
    if (!declaredBand) continue;

    // A generator may put the HTML marker on its own line immediately before
    // the sentence. In that shape `splitSentences` yields the marker as a
    // separate fragment, but the nearby-comment rule correctly associates it
    // with this claim. Extend the exact rewrite anchor back to the marker so
    // the persisted body can be changed rather than merely audited.
    const matchText = nearestComment && nearestComment.start < sent.start
      ? markdown.slice(nearestComment.start, sent.end).trim()
      : sent.text.trim();
    const excerpt = matchText.replace(CONFIDENCE_COMMENT_RE, '').trim();
    if (excerpt.length < 15) continue;
    if (isSentenceFragment(excerpt)) continue;
    // A flattened object is machine serialization, not a claim. Live
    // 2026-08-05: "- System: Wastewater | Total Capacity: Data not available |
    // …" was scored as over-claiming confidence.
    if (isSerializedRecord(excerpt)) continue;
    // A QUESTION asserts nothing, so it can neither over- nor under-claim
    // confidence. The absence detector has always guarded this; caveat did
    // not, and scored "- Claim: Have bridge load capacities … been verified?"
    // as a high-confidence claim.
    if (excerpt.endsWith('?')) continue;

    claims.push({
      id: `claim-${claimIdx++}`,
      excerpt,
      declaredBand,
      declaredSource,
      matchText: declaredSource === 'html-comment' ? matchText : excerpt,
      refIndices: extractRefIndices(sent.text),
      lineStart: lineNumberAt(markdown, sent.start),
      lineEnd: lineNumberAt(markdown, sent.end),
      startOffset: sent.start,
      endOffset: sent.end,
    });
  }
  return claims;
}

/**
 * True when the "sentence" is a split artifact rather than an assertion.
 *
 * `splitSentences` breaks on `.` and cannot see that a period sits inside a
 * parenthetical or an abbreviation, so a clause like
 *   "…(flood zone, elevation), and likely result in higher insurance premiums."
 * arrives as a fragment beginning `, elevation)`. A fragment states nothing, so
 * scoring its confidence is meaningless — and because the taxonomy reads a bare
 * "likely" as declaring MODERATE confidence, an unscoreable fragment reliably
 * produced a 2-band gap and BLOCKED the report (live 2026-08-04 Redhead).
 *
 * Detects only what cannot begin an English sentence: leading clause
 * punctuation, an unmatched closing bracket, or a leading coordinator. A
 * genuine claim never opens this way, so this cannot mask a real over-claim.
 */
export function isSentenceFragment(text: string): boolean {
  const t = text.trim();
  if (!t) return true;
  if (/^[,;:)\]}]/.test(t)) return true;
  if (/^(?:and|but|or|nor|yet|so)\b/i.test(t)) return true;
  // A closing bracket with no opener is the tail of a split parenthetical.
  const closers = (t.match(/[)\]]/g) ?? []).length;
  const openers = (t.match(/[([]/g) ?? []).length;
  return closers > openers;
}

/**
 * True when the sentence is framed as a conditional future rather than a
 * present assertion — "Connection WOULD LIKELY BE SOUGHT at 230 kV".
 *
 * The taxonomy maps a bare "likely" to MODERATE confidence, but inside a
 * `would …` construction "likely" softens a hypothetical; the sentence asserts
 * nothing about the present. Reading it as a moderate-confidence claim and then
 * scoring it against citation evidence produced a 2-band gap and blocked the
 * live Redhead report for a sentence that is, if anything, over-hedged.
 */
export function isConditionalFutureFraming(text: string): boolean {
  return /\bwould\s+(?:\w+\s+){0,3}(?:be|have|require|need|result|involve)\b/i.test(text)
    || /\bwould\s+(?:likely|probably|typically|generally)\b/i.test(text);
}

/**
 * True when a sentence names its own (non-authoritative) source or marks
 * itself unverified — i.e. it declares its own limited confidence.
 */
export function isProvenanceQualified(text: string): boolean {
  return /\b(?:per\s+(?:the\s+)?developer|developer[- ](?:stated|provided|reported|documents?)|according\s+to\s+(?:the\s+)?developer|unverified|unconfirmed|to\s+be\s+confirmed|should\s+be\s+(?:confirmed|verified)|pending\s+confirmation|not\s+yet\s+verified|indicative(?:\s+only)?|planning\s+defaults?|planning\s+assumptions?|illustrative|uploaded\s+(?:blank\s+)?template|not\s+(?:yet\s+)?established(?:\s+in\s+verified\s+(?:utility\s+)?records)?|regional\s+(?:generation\s+)?context|historical\s+generation[- ]queue\s+context|not\s+a\s+load-connection\s+timetable|literature\s+notes|typically\s+range|screening\s+context)\b/i
    .test(text);
}

/**
 * A publish-blocking claim supported only by tier-3 references has one safe,
 * deterministic repair path: lower the phrase to the evidence scorer's own
 * speculative band. This does not waive the finding. It changes the
 * reader-visible assertion (for example, "suggests" -> "may suggest") and
 * the gate immediately re-verifies the changed report.
 *
 * Keep this deliberately narrower than general caveat autocorrect. Mixed or
 * missing reference sets, warn-only gaps, and phrases with no grammatical
 * `SAFE_DOWNGRADE` remain untouched and therefore remain visible to the
 * ordinary audit policy.
 */
function isSafelyRewritableTier3Block(finding: CaveatHonestyFinding): boolean {
  const tiers = finding.evidenceBreakdown.refTiers;
  return finding.severity === 'block'
    && finding.actualBand === 'speculative'
    && tiers.length > 0
    && tiers.every(({ tier }) => tier === 3)
    && typeof finding.suggestedRewrite === 'string'
    && finding.suggestedRewrite.length > 0;
}

/** The weaker (lower-order) of two bands. */
export function weakerBand(a: ConfidenceBand, b: ConfidenceBand): ConfidenceBand {
  return CONFIDENCE_BANDS[a].order <= CONFIDENCE_BANDS[b].order ? a : b;
}

function severityForGap(gap: number): 'block' | 'warn' | null {
  if (gap >= 2) return 'block';
  if (gap === 1) return 'warn';
  return null;
}

/** True when surrounding text uses a softened conditional phrase. */
export function shouldSkipConditionalFinding(text: string): boolean {
  return CONDITIONAL_SOFTENED_PATTERN.test(text);
}

const EMPTY_BREAKDOWN: ClaimConfidenceScore['breakdown'] = {
  tier1Score: 0,
  tier2Score: 0,
  tier3Score: 0,
  multiSourceBonus: 0,
  brokenCitePenalty: 0,
  provenanceGapPenalty: 0,
  refTiers: [],
};

/** Detect bare "conditional" without declared caveat band or softened form. */
export function detectUnhedgedConditionalFindings(markdown: string): CaveatHonestyFinding[] {
  const findings: CaveatHonestyFinding[] = [];
  const sentences = splitSentences(markdown);
  let idx = 0;
  for (const sent of sentences) {
    if (!UNHEDGED_CONDITIONAL_PATTERN.test(sent.text)) continue;
    if (detectDeclaredBandFromPhrases(sent.text)) continue;
    if (shouldSkipConditionalFinding(sent.text)) {
      void logAuditEvent({
        type: 'softened-conditional-skipped',
        timestamp: new Date().toISOString(),
        payload: { excerpt: sent.text.slice(0, 120) },
      });
      continue;
    }
    findings.push({
      claimId: `conditional-${idx++}`,
      severity: 'warn',
      declaredBand: 'moderate-confidence',
      actualBand: 'speculative',
      bandGap: 1,
      excerpt: sent.text.slice(0, 280),
      message: 'Sentence uses "conditional" without an explicit caveat band or softened form.',
      lineStart: lineNumberAt(markdown, sent.start),
      lineEnd: lineNumberAt(markdown, sent.end),
      evidenceBreakdown: EMPTY_BREAKDOWN,
      kind: 'unhedged-conditional',
    });
  }
  return findings;
}

export function verifyCaveatHonesty(
  report: SiteRagReport,
  evidence: CitationEvidenceCache,
): CaveatHonestyFinding[] {
  const text = reportFullText(report);
  const claims = extractCaveatedClaims(text);
  const findings: CaveatHonestyFinding[] = [...detectUnhedgedConditionalFindings(text)];

  for (const claim of claims) {
    const scored = scoreClaimConfidence(
      {
        text: claim.excerpt,
        refIndices: claim.refIndices,
        sectionId: claim.sectionId,
        lineNumber: claim.lineStart,
      },
      report,
      evidence,
    );
    /*
     * PROVENANCE-AWARE DECLARED BAND (2026-08-05) — a claim that names its
     * own source and marks it unverified has ALREADY declared low confidence,
     * whatever the surrounding verbs sound like. Scoring it as "high
     * confidence vs speculative evidence" is a false over-claim: the live
     * 2026-08-04 Redhead report drew ten BLOCKING findings of this shape on
     * sentences such as "per developer documents (unverified), the site has
     * direct pipeline access" — exactly the attribution discipline the
     * provenance rules ask the generator to write. Capping the declared band
     * for self-qualifying sentences keeps the detector blocking on genuine
     * over-claims (confident prose with no attribution) without punishing
     * honest reporting.
     */
    const selfLimiting =
      isProvenanceQualified(claim.excerpt) || isConditionalFutureFraming(claim.excerpt);
    const declaredBand = selfLimiting
      ? weakerBand(claim.declaredBand, 'preliminary')
      : claim.declaredBand;
    const gap = bandGap(declaredBand, scored.band);
    let severity = severityForGap(gap);
    if (!severity) continue;
    /*
     * UNCITED-CLAIM DEMOTION (2026-08-11) — an uncited hedged sentence cannot
     * DEMONSTRATE an over-claim. With no references, the scorer's band is not
     * "the evidence contradicts this claim"; it is "no evidence was cited",
     * so the gap measures absence, not falsehood. Under the severity policy
     * ("block on demonstrable untruth only" — qaSeverityPolicy.ts) that is a
     * quality signal: warn, keep the suggested rewrite (which also makes the
     * CaveatHonestyPanel's rewrite affordance available for it).
     *
     * Live 2026-08-11 (Redhead): every one of the 8 generated reports was
     * quality-held (export disabled) on ≥1 uncited "likely/expected" analytic
     * sentence, and regeneration reproduced the hold verbatim — an infinite
     * ~1-hour retry loop. Claims that DO cite references still block on a
     * ≥2-band gap: their refs are named and demonstrably fail to support the
     * declared band.
     */
    if (severity === 'block' && claim.refIndices.length === 0) severity = 'warn';

    const declaredLabel = CONFIDENCE_BANDS[claim.declaredBand].label;
    const actualLabel = CONFIDENCE_BANDS[scored.band].label;
    const suggestedRewrite = buildSuggestedRewrite(
      claim.matchText,
      scored.band,
      claim.declaredBand,
      claim.declaredSource,
    );
    findings.push({
      claimId: claim.id,
      severity,
      declaredBand: claim.declaredBand,
      actualBand: scored.band,
      bandGap: gap,
      excerpt: claim.excerpt.slice(0, 280),
      message: `Claim declares ${declaredLabel} but evidence supports ${actualLabel} (${gap} band(s) over-claimed).`,
      ...(claim.sectionId !== undefined ? { sectionId: claim.sectionId } : {}),
      lineStart: claim.lineStart,
      lineEnd: claim.lineEnd,
      evidenceBreakdown: scored.breakdown,
      ...(suggestedRewrite !== undefined ? { suggestedRewrite } : {}),
      matchText: claim.matchText,
      kind: 'band-gap',
    });
  }
  return findings;
}

/**
 * Phrase-level downgrade table (2026-08-14). Substituting a band LABEL for an
 * arbitrary matched phrase destroyed sentences: the phrase map mixes
 * ADJECTIVES ("confirmed", "verified") with VERBS ("indicates", "expected
 * to", "may"), and replacing a verb with the noun-phrase label deletes the
 * predicate —
 *   "Verified utility correspondence indicates a 300 MW allocation."
 *   → "speculative utility correspondence speculative a 300 MW allocation."
 *
 * A rewrite may only substitute a phrase whose replacement is GRAMMATICALLY
 * equivalent and MEANING-PRESERVING apart from the confidence it asserts.
 * Anything not in this table has no safe rewrite, and the finding survives
 * honestly instead of being papered over.
 */
const SAFE_DOWNGRADE: Record<string, Partial<Record<ConfidenceBand, string>>> = {
  confirmed: { 'moderate-confidence': 'indicated', preliminary: 'preliminarily indicated', speculative: 'unverified' },
  verified: { 'moderate-confidence': 'indicated', preliminary: 'preliminary', speculative: 'unverified' },
  definitively: { 'moderate-confidence': 'likely', preliminary: 'preliminarily', speculative: 'possibly' },
  conclusively: { 'moderate-confidence': 'likely', preliminary: 'preliminarily', speculative: 'possibly' },
  likely: { preliminary: 'preliminarily expected', speculative: 'possibly' },
  probable: { preliminary: 'preliminary', speculative: 'possible' },
  indicates: { preliminary: 'preliminarily indicates', speculative: 'may indicate' },
  suggests: { preliminary: 'preliminarily suggests', speculative: 'may suggest' },
  'expected to': { preliminary: 'preliminarily expected to', speculative: 'may' },
  estimated: { speculative: 'roughly estimated' },
  preliminary: { speculative: 'unverified' },
};

/**
 * Build a meaning-preserving downgrade for ONE claim, or `undefined` when no
 * safe substitution exists. Only the phrase that produced the claim's
 * DECLARED band is touched — hedges belonging to other clauses are never
 * rewritten (they were not the over-claim).
 */
function buildSuggestedRewrite(
  excerpt: string,
  actualBand: ConfidenceBand,
  declaredBand: ConfidenceBand,
  declaredSource: CaveatedClaim['declaredSource'] = 'caveat-phrase',
): string | undefined {
  const source = excerpt.replace(CONFIDENCE_COMMENT_RE, '').trim();
  let commentRewrite: string | undefined;
  if (declaredSource === 'html-comment') {
    const comment = new RegExp(CONFIDENCE_COMMENT_RE.source, 'i').exec(excerpt);
    if (!comment || comment.index === undefined) return undefined;
    const band = actualBand === 'high-confidence'
      ? 'high'
      : actualBand === 'moderate-confidence'
        ? 'moderate'
        : actualBand;
    const replacement = `<!-- confidence: ${band} -->`;
    commentRewrite = excerpt.slice(0, comment.index)
      + replacement
      + excerpt.slice(comment.index + comment[0].length);
  }
  const phraseSource = commentRewrite ?? source;
  // Only the DECLARED band's phrases are candidates, and only the first
  // asserting occurrence. Verification requirements and negations stay intact.
  for (const re of CAVEAT_PHRASE_MAP[declaredBand]) {
    const m = [...phraseSource.matchAll(new RegExp(re.source, 'gi'))]
      .find(match => !isNonAssertedVerification(phraseSource, match.index!, match[0]));
    if (!m || m.index === undefined) continue;
    const matched = m[0];
    const replacement = SAFE_DOWNGRADE[matched.toLowerCase()]?.[actualBand];
    if (!replacement) continue; // no safe swap for this phrase → keep looking
    // Modal replacement must consume the passive copula as well:
    //   "the trend is expected to form" -> "the trend may form"
    // Replacing only "expected to" produced the ungrammatical "is may form"
    // in the live Redhead Public Sentiment repair replay. This special case is
    // deliberately narrow; other phrase replacements keep their exact anchor.
    const passiveExpected = matched.toLowerCase() === 'expected to'
      && replacement.toLowerCase() === 'may'
      ? phraseSource.slice(0, m.index).match(/\b(?:is|are|was|were)\s+(not\s+)?$/i)
      : null;
    // A negated confidence claim cannot use a positive-phrase downgrade:
    // "No grid capacity has been confirmed" -> "...been unverified"
    // reverses the uncertainty. Keep the original claim and its audit finding
    // when no meaning-preserving rewrite is available. The existing passive
    // modal rule is safe: "is not expected to" -> "may not".
    const clauseBeforePhrase = phraseSource.slice(0, m.index)
      .split(/[;:]|\b(?:but|yet|however|whereas)\b/i).at(-1) ?? '';
    // P3-15 — an absence or lack word guards the rewrite only inside the
    // phrase's own comma-delimited clause. "The absence of wetlands has been
    // confirmed" keeps its wording; an introductory "Despite the lack of a
    // water right, the utility has a confirmed allocation" still gets the
    // safe downgrade of the unrelated over-claim.
    const negated = /\b(?:no|not|never|neither|without|cannot)\b|\b\w+n['’]t\b/i.test(clauseBeforePhrase)
      || /\b(?:absence|absent|lack(?:s|ed|ing)?)\b/i.test(clauseBeforePhrase.split(',').at(-1) ?? '');
    if (negated && !passiveExpected?.[1]) continue;
    const replacementStart = passiveExpected?.index ?? m.index;
    const grammaticalReplacement = passiveExpected?.[1] ? `${replacement} not` : replacement;
    // Preserve leading capitalization of the original phrase.
    const cased = /^[A-Z]/.test(matched)
      ? grammaticalReplacement.charAt(0).toUpperCase() + grammaticalReplacement.slice(1)
      : grammaticalReplacement;
    return agreeIndefiniteArticle(phraseSource.slice(0, replacementStart), cased)
      + cased
      + phraseSource.slice(m.index + matched.length);
  }
  return commentRewrite;
}

/**
 * Keep an indefinite article that directly precedes a substituted phrase in
 * agreement with the replacement: "a confirmed allocation" becomes "an
 * unverified allocation", and "an estimated load" becomes "a roughly estimated
 * load". Only a lowercase article, or a capitalized article that opens the
 * sentence, is adjusted; a capital letter elsewhere is a label ("Zone A",
 * "Appendix A") and is never touched.
 */
function agreeIndefiniteArticle(prefix: string, replacement: string): string {
  const article = /(^|[^\w'’-])(a|an|A|An)(\s+)$/.exec(prefix);
  if (!article) return prefix;
  const [, boundary = '', word = '', spacing = ''] = article;
  const articleStart = article.index + boundary.length;
  const capitalized = word.startsWith('A');
  if (capitalized && !/^\s*(?:[-*•]\s+|<!--[\s\S]*?-->\s*)?$/.test(prefix.slice(0, articleStart))) {
    return prefix;
  }
  const wanted = /^[aeiou]/i.test(replacement) ? 'an' : 'a';
  if (word.toLowerCase() === wanted) return prefix;
  const agreed = capitalized ? wanted.charAt(0).toUpperCase() + wanted.slice(1) : wanted;
  return prefix.slice(0, articleStart) + agreed + spacing;
}

/** Drop citation tokens and the space they leave before punctuation. */
function withoutRefs(value: string): string {
  return value
    .replace(/\s*\[Ref\s+\d+\]/gi, '')
    .replace(/\s+([,.;:!?])/g, '$1')
    .trim();
}

/**
 * The serialized-row prefix in front of a claim scored from a flattened
 * record: an optional run of earlier cells ending in "|", an optional bullet,
 * and the claim cell's label ("- Text: ", "sed-section:x | Text: ").
 */
const ROW_LABEL_PREFIX_RE = /^(?:[^|\n]*\|\s*)*(?:[-*•]\s+)?[A-Z][A-Za-z ]{0,30}:\s+/;

export function applyCaveatRewrites(
  markdown: string,
  findings: CaveatHonestyFinding[],
  mode: 'autocorrect' | 'preview' = 'autocorrect',
): { markdown: string; applied: number; skippedBlocks: number } {
  void mode;
  /*
   * TRUTHFUL DOWNGRADE (2026-08-14, Redhead sweep conclusion) — BLOCK
   * findings are now eligible too. The old hasBlocks bail made the gate
   * self-defeating: the generator systematically writes claim confidence
   * 1–2 bands above the evidence grade (live: 20/18/13/6 such findings per
   * report across water/environmental/PSR/supply-chain, on EVERY
   * regeneration), so every report held forever on a defect the machinery
   * already knows how to fix — `suggestedRewrite` replaces the over-claiming
   * phrase with the evidence band's own label, which makes the sentence TRUE.
   * Fixing the untruth beats shipping nothing: rewritten claims re-verify
   * clean; claims whose rewrite cannot land keep their finding honestly.
   */
  const eligible = findings.filter((f) => f.suggestedRewrite);
  if (eligible.length === 0) {
    return { markdown, applied: 0, skippedBlocks: 0 };
  }

  let out = markdown;
  let applied = 0;
  // EXACT-MATCH ANCHORING (2026-08-14). The old splice located an 80-char
  // PREFIX and then replaced `excerpt.length` characters from there — three
  // ways to corrupt the body: (a) a prefix shared by two sentences rewrote
  // the WRONG one; (b) `excerpt` is truncated at 280 chars while the rewrite
  // is built from the full sentence, so the tail was duplicated; (c) two
  // findings sharing an excerpt double-applied. Anchor on the full,
  // untruncated sentence, require an exact match at the index, and dedupe.
  const seen = new Set<string>();
  for (const f of eligible) {
    const target = f.matchText ?? f.excerpt;
    if (!f.suggestedRewrite || !target) continue;
    if (seen.has(target)) continue;
    seen.add(target);
    if (out.includes(f.suggestedRewrite)) continue; // idempotent
    let anchoredTarget = target;
    let anchoredRewrite = f.suggestedRewrite;
    let idx = out.indexOf(anchoredTarget);
    if (idx === -1) {
      /*
       * STRUCTURED PROJECTION ANCHOR (2026-08-28, live Redhead PSR).
       * A finding can be rendered once as cited narrative and again in a
       * structured `{ text, sources }` row. The row's `text` omits `[Ref N]`
       * because citations live in sibling `sources`, so exact anchoring missed
       * it and left "is expected to" behind even though re-verification passed
       * (serialized rows are intentionally not scored as prose). Match ONLY
       * the otherwise-verbatim citationless sentence, and keep it citationless
       * on replacement. The 40-char floor prevents a short generic phrase from
       * becoming a broad rewrite anchor.
       */
      const citationlessTarget = withoutRefs(target);
      const citationlessRewrite = withoutRefs(f.suggestedRewrite);
      if (citationlessTarget.length >= 40 && citationlessTarget !== target) {
        const citationlessIdx = out.indexOf(citationlessTarget);
        if (citationlessIdx !== -1 && !out.includes(citationlessRewrite)) {
          anchoredTarget = citationlessTarget;
          anchoredRewrite = citationlessRewrite;
          idx = citationlessIdx;
        }
      }
    }
    if (idx === -1) {
      /*
       * ROW-LABEL ANCHOR (2026-09-30, live Holly Ridge feasibility ka01hs).
       * A claim scored from its SERIALIZED row carries the row's cell label
       * ("- Text: …", "sed-section:x | Text: …") in both the target and the
       * rewrite, while the structured fields that hold the sentence
       * (`claims[].text`, `recommendations[].rationale`) do not. Neither
       * anchor above can land, so the TM2500 lead-time claim kept its
       * moderate-confidence "indicates" and held the report. Anchor on the
       * label-less sentence (then its citationless form) ONLY when the rewrite
       * carries the identical label, with the same 40-char floor.
       */
      const label = ROW_LABEL_PREFIX_RE.exec(target)?.[0];
      if (label && f.suggestedRewrite.startsWith(label)) {
        const bareTarget = target.slice(label.length);
        const bareRewrite = f.suggestedRewrite.slice(label.length);
        for (const [candidate, replacement] of [
          [bareTarget, bareRewrite],
          [withoutRefs(bareTarget), withoutRefs(bareRewrite)],
        ] as const) {
          if (candidate.length < 40 || candidate === replacement) continue;
          const at = out.indexOf(candidate);
          if (at === -1 || out.includes(replacement)) continue;
          anchoredTarget = candidate;
          anchoredRewrite = replacement;
          idx = at;
          break;
        }
      }
    }
    if (idx === -1) continue;                        // no safe verbatim anchor → skip
    out = out.slice(0, idx) + anchoredRewrite + out.slice(idx + anchoredTarget.length);
    applied += 1;
  }
  return { markdown: out, applied, skippedBlocks: 0 };
}

/** Recursively rewrite every narrative string field on the report,
 *  skipping non-prose subtrees (datasets / references / metadata). Mirrors
 *  `applyPrecedenceFixesToReport`'s `walkStringFields` — caveat findings carry
 *  excerpts extracted from the assembled structured body, so the substring
 *  replace in `applyCaveatRewrites` must run over each structured prose field
 *  rather than the (non-existent) `report.markdown` blob. */
function walkStringFields(
  obj: unknown,
  fn: (path: string, value: string) => string,
  path = '',
): unknown {
  if (typeof obj === 'string') return fn(path, obj);
  if (Array.isArray(obj)) {
    return obj.map((item, i) => walkStringFields(item, fn, `${path}[${i}]`));
  }
  if (obj && typeof obj === 'object') {
    if (path.includes('datasets') || path.includes('references') || path.includes('metadata')) {
      return obj;
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      out[k] = walkStringFields(v, fn, path ? `${path}.${k}` : k);
    }
    return out;
  }
  return obj;
}

/**
 * Apply caveat-honesty rewrites across the report's STRUCTURED prose fields and
 * return the rewritten report plus the total number of excerpt substitutions
 * applied.
 *
 * Typed/structured reports have NO `markdown` blob (it is a legacy-only field);
 * the body lives in `executiveSummary`, `summary`, `conclusion`, the EIA/power
 * string sections, structured findings, etc. This walker runs the same
 * substring-replace `applyCaveatRewrites` performs over each of those string
 * fields, so the per-claim caveat softening actually lands in the persisted
 * report. It also rewrites a legacy `markdown` blob when one is present.
 *
 * Returns `{ report, applied }`. When `applied === 0` (block findings present,
 * no eligible warn findings, or the excerpt was not found verbatim in any
 * field) the returned `report` is referentially equal to the input — callers
 * can detect a genuine no-op and surface it instead of persisting a no-change.
 */
/** `waterScreening`, `waterScreeningView…` and `waterScreeningBasis…` report paths. */
const DETERMINISTIC_WATER_SCREEN_PATH = /^waterScreening(?:View|Basis)?(?:$|[.[])/;

export function applyCaveatRewritesToReport(
  report: SiteRagReport,
  findings: CaveatHonestyFinding[],
  mode: 'autocorrect' | 'preview' = 'autocorrect',
): { report: SiteRagReport; applied: number } {
  void mode;
  const hasBlocks = findings.some((f) => f.severity === 'block');
  // TRUTHFUL DOWNGRADE — blocks are eligible; see applyCaveatRewrites.
  void hasBlocks;
  const eligible = findings.filter((f) => f.suggestedRewrite);
  if (eligible.length === 0) {
    return { report, applied: 0 };
  }

  let applied = 0;
  const rewritten = walkStringFields(report, (path, text) => {
    // Skip the qaFindings audit subtree — those strings (excerpts / messages)
    // are not the report body and must not be mutated under the reader's feet.
    if (path.startsWith('qaFindings')) return text;
    // The deterministic "Modeled Water Use" section (text, reader view and
    // basis) is copied from the frozen SED screen, never authored prose. A
    // band-level rewrite there changes a model-stated condition (it turned
    // "only in verified full-capacity dry mode" into "unverified"), and it
    // could only reach the saved text, not the reader view built beside it.
    if (DETERMINISTIC_WATER_SCREEN_PATH.test(path)) return text;
    const { markdown: next, applied: count } = applyCaveatRewrites(text, findings, 'autocorrect');
    applied += count;
    return next;
  }) as SiteRagReport;

  // Legacy markdown blob (when a report still carries one).
  const md = (report as SiteRagReport & { markdown?: string }).markdown;
  if (typeof md === 'string' && md.length > 0) {
    const { markdown: nextMd, applied: count } = applyCaveatRewrites(md, findings, 'autocorrect');
    if (count > 0) {
      (rewritten as SiteRagReport & { markdown?: string }).markdown = nextMd;
      applied += count;
    }
  }

  return applied > 0 ? { report: rewritten, applied } : { report, applied: 0 };
}

export interface ApplyCaveatHonestyGateOptions {
  autocorrect?: boolean;
  /**
   * Apply only meaning-preserving rewrites for blocking claims whose cited
   * evidence is exclusively tier 3. Intended for the generation boundary;
   * unlike broad autocorrect, it cannot touch warn-only or mixed-tier claims.
   */
  normalizeTier3Blocks?: boolean;
  /** Generation-only bounded convergence; omitted keeps the existing one-pass audit. */
  maxNormalizePasses?: number;
  evidence?: CitationEvidenceCache;
  findings?: CaveatHonestyFinding[];
}

export function applyCaveatHonestyGate(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
  opts: ApplyCaveatHonestyGateOptions = {},
): SiteRagReport {
  const evidence = opts.evidence ?? buildCitationEvidenceCache(sed);
  const maxPasses = Math.min(3, Math.max(1, Math.floor(opts.maxNormalizePasses ?? 1)));
  if (opts.normalizeTier3Blocks && maxPasses > 1) {
    let current = report;
    let rewritesApplied = 0;
    const history = new Map<string, NonNullable<NonNullable<NonNullable<SiteRagReport['qaFindings']>['caveatHonesty']>['downgraded']>[number]>();
    const historyKey = (before: string, row: unknown) => before ? before.normalize('NFKC').replace(/\s+/g, ' ').trim() : JSON.stringify(row);
    for (const row of report.qaFindings?.caveatHonesty?.downgraded ?? []) history.set(historyKey(row.before, row), row);
    const tokens = (body: SiteRagReport) => reportFullText(body).match(new RegExp(NUMBERED_REF_TOKEN_RE.source, 'gi')) ?? [];
    for (let pass = 0; pass < maxPasses; pass++) {
      const findings = pass === 0 && opts.findings ? opts.findings : verifyCaveatHonesty(current, evidence);
      const next = applyCaveatHonestyGate(current, sed, { ...opts, evidence, findings,
        autocorrect: pass === 0 ? opts.autocorrect : false, maxNormalizePasses: 1 });
      // Convergence must not weaken primary labels or change compound citation spelling.
      if (JSON.stringify(tokens(current)) !== JSON.stringify(tokens(next))) break;
      for (const row of next.qaFindings?.caveatHonesty?.downgraded ?? []) {
        const before = findings.find(f => f.claimId === row.claimId && f.excerpt === row.before)?.matchText ?? row.before;
        const key = historyKey(before, row);
        history.set(key, { ...row, before });
      }
      const changed = reportFullText(current) !== reportFullText(next);
      if (changed) rewritesApplied += next.qaFindings?.caveatHonesty?.rewritesApplied ?? 0;
      current = next;
      if (!changed) break;
    }
    if (!current.qaFindings?.caveatHonesty) return current;
    return { ...current, qaFindings: { ...current.qaFindings, caveatHonesty: { ...current.qaFindings.caveatHonesty,
      downgraded: [...history.values()], rewritesApplied, autocorrectApplied: rewritesApplied > 0 } } };
  }
  const findings = opts.findings ?? verifyCaveatHonesty(report, evidence);
  if (findings.length === 0) {
    // ZOMBIE-STAMP CLEAR (2026-08-14) — mirrors the R-78 fix: an update-mode
    // draft INHERITS its parent's qaFindings, so a clean pass that returned
    // the untouched report left a stale caveatHonesty stamp alive forever.
    const inherited = report.qaFindings?.caveatHonesty?.findings;
    if (inherited?.length) {
      return {
        ...report,
        qaFindings: {
          ...report.qaFindings,
          caveatHonesty: {
            findings: [],
            claimsScanned: extractCaveatedClaims(reportFullText(report)).length,
            checkedAt: new Date().toISOString(),
            autocorrectApplied: false,
            blockCount: 0,
            warnCount: 0,
          },
        },
      };
    }
    return report;
  }

  let out: SiteRagReport = { ...report };
  let autocorrectApplied = false;
  let rewritesApplied = 0;
  let effectiveFindings = findings;

  /*
   * TRUTHFUL DOWNGRADE (2026-08-14) — apply the per-claim rewrite over the
   * STRUCTURED body (typed reports have no `markdown` blob; the old
   * markdown-only path never touched them, which is why the class exploded
   * across every type), for warns AND blocks, then RE-VERIFY: a rewritten
   * claim now declares the evidence band's own label, so its gap is 0 and
   * it produces no finding. Only claims whose rewrite could not land keep
   * their finding — an honest residue, never a hidden one.
   */
  const rewriteCandidates = opts.autocorrect
    ? findings
    : opts.normalizeTier3Blocks
      ? findings.filter(isSafelyRewritableTier3Block)
      : [];
  if (rewriteCandidates.length > 0) {
    const { report: rewritten, applied } = applyCaveatRewritesToReport(out, rewriteCandidates, 'autocorrect');
    if (applied > 0) {
      out = rewritten;
      autocorrectApplied = true;
      rewritesApplied = applied;
      effectiveFindings = verifyCaveatHonesty(out, evidence);
    }
  }

  const blockCount = effectiveFindings.filter((f) => f.severity === 'block').length;
  const warnCount = effectiveFindings.filter((f) => f.severity === 'warn').length;

  console.info('[R-75 caveat honesty]', {
    reportId: out.id,
    blockCount,
    warnCount,
    autocorrectApplied,
    rewritesApplied,
  });

  return {
    ...out,
    qaFindings: {
      ...(out.qaFindings ?? {}),
      caveatHonesty: {
        findings: effectiveFindings,
        // AUDIT TRAIL (2026-08-14): claims the rewrite downgraded must stay
        // visible — otherwise the report shows a clean caveat gate with no
        // record of which sentences were altered under the reader's feet.
        downgraded: findings
          .filter((f) => {
            if (!autocorrectApplied || !rewriteCandidates.includes(f) || !f.suggestedRewrite) return false;
            const before = f.matchText ?? f.excerpt;
            const originalText = reportFullText(report);
            const correctedText = reportFullText(out);
            // Bind receipts to an applied before/after span, not ordinal claim
            // ids: a surviving warning can keep its id, or an untouched warning
            // can inherit a removed claim's id after remeasurement.
            return before !== f.suggestedRewrite && originalText.includes(before)
              && !correctedText.includes(before) && correctedText.includes(f.suggestedRewrite);
          })
          .map((f) => ({
            claimId: f.claimId,
            declaredBand: f.declaredBand,
            actualBand: f.actualBand,
            before: f.excerpt,
            after: f.suggestedRewrite ?? '',
          })),
        claimsScanned: extractCaveatedClaims(reportFullText(report)).length,
        checkedAt: new Date().toISOString(),
        autocorrectApplied,
        rewritesApplied,
        blockCount,
        warnCount,
      },
    },
  };
}
